import { normalizeSafeNSName } from "../utils/safens.mjs";
import { isValidNamedPackage } from "../utils/named-packages.mjs";
import { isValidSafeAddress, isValidSafeObjectId, isValidTransactionDigest, normalizeSafeAddress, normalizeSafeObjectId } from "../utils/safe-types.mjs";
import { BaseClient } from "../client/client.mjs";
import { hasMvrName } from "../client/mvr.mjs";
import { JsonRpcHTTPTransport } from "./http-transport.mjs";
import { isCoinReservationDigest } from "../utils/coin-reservation.mjs";
import { isTransaction } from "../transactions/Transaction.mjs";
import { JSONRpcCoreClient } from "./core.mjs";
import { fromBase58, toBase64, toHex } from "@safecoin/bcs";
//#region src/jsonRpc/client.ts
const SAFE_CLIENT_BRAND = Symbol.for("@safecoin/SafeJsonRpcClient");
/**
* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
*/
function isSafeJsonRpcClient(client) {
	return typeof client === "object" && client !== null && client[SAFE_CLIENT_BRAND] === true;
}
/**
* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
*/
var SafeJsonRpcClient = class extends BaseClient {
	get [SAFE_CLIENT_BRAND]() {
		return true;
	}
	/**
	* Establish a connection to a Safe RPC endpoint
	*
	* @param options configuration options for the API Client
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	constructor(options) {
		super({ network: options.network });
		this.jsonRpc = this;
		this.transport = options.transport ?? new JsonRpcHTTPTransport({ url: options.url });
		this.core = new JSONRpcCoreClient({
			jsonRpcClient: this,
			mvr: options.mvr
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getRpcApiVersion({ signal } = {}) {
		return (await this.transport.request({
			method: "rpc.discover",
			params: [],
			signal
		})).info.version;
	}
	/**
	* Get all Coin<`coin_type`> objects owned by an address.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCoins({ coinType, owner, cursor, limit, signal }) {
		if (!owner || !isValidSafeAddress(normalizeSafeAddress(owner))) throw new Error("Invalid Safe address");
		if (coinType && hasMvrName(coinType)) coinType = (await this.core.mvr.resolveType({
			type: coinType,
			signal
		})).type;
		const result = await this.transport.request({
			method: "safex_getCoins",
			params: [
				owner,
				coinType,
				cursor,
				limit
			],
			signal
		});
		return {
			...result,
			data: result.data.filter((coin) => !isCoinReservationDigest(coin.digest))
		};
	}
	/**
	* Get all Coin objects owned by an address.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getAllCoins(input) {
		if (!input.owner || !isValidSafeAddress(normalizeSafeAddress(input.owner))) throw new Error("Invalid Safe address");
		const result = await this.transport.request({
			method: "safex_getAllCoins",
			params: [
				input.owner,
				input.cursor,
				input.limit
			],
			signal: input.signal
		});
		return {
			...result,
			data: result.data.filter((coin) => !isCoinReservationDigest(coin.digest))
		};
	}
	/**
	* Get the total coin balance for one coin type, owned by the address owner.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getBalance({ owner, coinType, signal }) {
		if (!owner || !isValidSafeAddress(normalizeSafeAddress(owner))) throw new Error("Invalid Safe address");
		if (coinType && hasMvrName(coinType)) coinType = (await this.core.mvr.resolveType({
			type: coinType,
			signal
		})).type;
		return await this.transport.request({
			method: "safex_getBalance",
			params: [owner, coinType],
			signal
		});
	}
	/**
	* Get the total coin balance for all coin types, owned by the address owner.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getAllBalances(input) {
		if (!input.owner || !isValidSafeAddress(normalizeSafeAddress(input.owner))) throw new Error("Invalid Safe address");
		return await this.transport.request({
			method: "safex_getAllBalances",
			params: [input.owner],
			signal: input.signal
		});
	}
	/**
	* Fetch CoinMetadata for a given coin type
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCoinMetadata({ coinType, signal }) {
		if (coinType && hasMvrName(coinType)) coinType = (await this.core.mvr.resolveType({
			type: coinType,
			signal
		})).type;
		return await this.transport.request({
			method: "safex_getCoinMetadata",
			params: [coinType],
			signal
		});
	}
	/**
	*  Fetch total supply for a coin
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getTotalSupply({ coinType, signal }) {
		if (coinType && hasMvrName(coinType)) coinType = (await this.core.mvr.resolveType({
			type: coinType,
			signal
		})).type;
		return await this.transport.request({
			method: "safex_getTotalSupply",
			params: [coinType],
			signal
		});
	}
	/**
	* Invoke any RPC method
	* @param method the method to be invoked
	* @param args the arguments to be passed to the RPC request
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async call(method, params, { signal } = {}) {
		return await this.transport.request({
			method,
			params,
			signal
		});
	}
	/**
	* Get Move function argument types like read, write and full access
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getMoveFunctionArgTypes({ package: pkg, module, function: fn, signal }) {
		if (pkg && isValidNamedPackage(pkg)) pkg = (await this.core.mvr.resolvePackage({
			package: pkg,
			signal
		})).package;
		return await this.transport.request({
			method: "safe_getMoveFunctionArgTypes",
			params: [
				pkg,
				module,
				fn
			],
			signal
		});
	}
	/**
	* Get a map from module name to
	* structured representations of Move modules
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getNormalizedMoveModulesByPackage({ package: pkg, signal }) {
		if (pkg && isValidNamedPackage(pkg)) pkg = (await this.core.mvr.resolvePackage({
			package: pkg,
			signal
		})).package;
		return await this.transport.request({
			method: "safe_getNormalizedMoveModulesByPackage",
			params: [pkg],
			signal
		});
	}
	/**
	* Get a structured representation of Move module
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getNormalizedMoveModule({ package: pkg, module, signal }) {
		if (pkg && isValidNamedPackage(pkg)) pkg = (await this.core.mvr.resolvePackage({
			package: pkg,
			signal
		})).package;
		return await this.transport.request({
			method: "safe_getNormalizedMoveModule",
			params: [pkg, module],
			signal
		});
	}
	/**
	* Get a structured representation of Move function
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getNormalizedMoveFunction({ package: pkg, module, function: fn, signal }) {
		if (pkg && isValidNamedPackage(pkg)) pkg = (await this.core.mvr.resolvePackage({
			package: pkg,
			signal
		})).package;
		return await this.transport.request({
			method: "safe_getNormalizedMoveFunction",
			params: [
				pkg,
				module,
				fn
			],
			signal
		});
	}
	/**
	* Get a structured representation of Move struct
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getNormalizedMoveStruct({ package: pkg, module, struct, signal }) {
		if (pkg && isValidNamedPackage(pkg)) pkg = (await this.core.mvr.resolvePackage({
			package: pkg,
			signal
		})).package;
		return await this.transport.request({
			method: "safe_getNormalizedMoveStruct",
			params: [
				pkg,
				module,
				struct
			],
			signal
		});
	}
	/**
	* Get all objects owned by an address
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getOwnedObjects(input) {
		if (!input.owner || !isValidSafeAddress(normalizeSafeAddress(input.owner))) throw new Error("Invalid Safe address");
		const filter = input.filter ? { ...input.filter } : void 0;
		if (filter && "MoveModule" in filter && isValidNamedPackage(filter.MoveModule.package)) filter.MoveModule = {
			module: filter.MoveModule.module,
			package: (await this.core.mvr.resolvePackage({
				package: filter.MoveModule.package,
				signal: input.signal
			})).package
		};
		else if (filter && "StructType" in filter && hasMvrName(filter.StructType)) filter.StructType = (await this.core.mvr.resolveType({
			type: filter.StructType,
			signal: input.signal
		})).type;
		return await this.transport.request({
			method: "safex_getOwnedObjects",
			params: [
				input.owner,
				{
					filter,
					options: input.options
				},
				input.cursor,
				input.limit
			],
			signal: input.signal
		});
	}
	/**
	* Get details about an object
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getObject(input) {
		if (!input.id || !isValidSafeObjectId(normalizeSafeObjectId(input.id))) throw new Error("Invalid Safe Object id");
		return await this.transport.request({
			method: "safe_getObject",
			params: [input.id, input.options],
			signal: input.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async tryGetPastObject(input) {
		return await this.transport.request({
			method: "safe_tryGetPastObject",
			params: [
				input.id,
				input.version,
				input.options
			],
			signal: input.signal
		});
	}
	/**
	* Batch get details about a list of objects. If any of the object ids are duplicates the call will fail
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async multiGetObjects(input) {
		input.ids.forEach((id) => {
			if (!id || !isValidSafeObjectId(normalizeSafeObjectId(id))) throw new Error(`Invalid Safe Object id ${id}`);
		});
		if (input.ids.length !== new Set(input.ids).size) throw new Error(`Duplicate object ids in batch call ${input.ids}`);
		return await this.transport.request({
			method: "safe_multiGetObjects",
			params: [input.ids, input.options],
			signal: input.signal
		});
	}
	/**
	* Get transaction blocks for a given query criteria
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async queryTransactionBlocks({ filter, options, cursor, limit, order, signal }) {
		if (filter && "MoveFunction" in filter && isValidNamedPackage(filter.MoveFunction.package)) filter = {
			...filter,
			MoveFunction: { package: (await this.core.mvr.resolvePackage({
				package: filter.MoveFunction.package,
				signal
			})).package }
		};
		return await this.transport.request({
			method: "safex_queryTransactionBlocks",
			params: [
				{
					filter,
					options
				},
				cursor,
				limit,
				(order || "descending") === "descending"
			],
			signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getTransactionBlock(input) {
		if (!isValidTransactionDigest(input.digest)) throw new Error("Invalid Transaction digest");
		return await this.transport.request({
			method: "safe_getTransactionBlock",
			params: [input.digest, input.options],
			signal: input.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async multiGetTransactionBlocks(input) {
		input.digests.forEach((d) => {
			if (!isValidTransactionDigest(d)) throw new Error(`Invalid Transaction digest ${d}`);
		});
		if (input.digests.length !== new Set(input.digests).size) throw new Error(`Duplicate digests in batch call ${input.digests}`);
		return await this.transport.request({
			method: "safe_multiGetTransactionBlocks",
			params: [input.digests, input.options],
			signal: input.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async executeTransactionBlock({ transactionBlock, signature, options, signal }) {
		return await this.transport.request({
			method: "safe_executeTransactionBlock",
			params: [
				typeof transactionBlock === "string" ? transactionBlock : toBase64(transactionBlock),
				Array.isArray(signature) ? signature : [signature],
				options
			],
			signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async signAndExecuteTransaction({ transaction, signer, ...input }) {
		let transactionBytes;
		if (transaction instanceof Uint8Array) transactionBytes = transaction;
		else {
			transaction.setSenderIfNotSet(signer.toSafeAddress());
			transactionBytes = await transaction.build({ client: this });
		}
		const { signature, bytes } = await signer.signTransaction(transactionBytes);
		return this.executeTransactionBlock({
			transactionBlock: bytes,
			signature,
			...input
		});
	}
	/**
	* Get total number of transactions
	*/
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getTotalTransactionBlocks({ signal } = {}) {
		const resp = await this.transport.request({
			method: "safe_getTotalTransactionBlocks",
			params: [],
			signal
		});
		return BigInt(resp);
	}
	/**
	* Getting the reference gas price for the network
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getReferenceGasPrice({ signal } = {}) {
		const resp = await this.transport.request({
			method: "safex_getReferenceGasPrice",
			params: [],
			signal
		});
		return BigInt(resp);
	}
	/**
	* Return the delegated stakes for an address
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getStakes(input) {
		if (!input.owner || !isValidSafeAddress(normalizeSafeAddress(input.owner))) throw new Error("Invalid Safe address");
		return await this.transport.request({
			method: "safex_getStakes",
			params: [input.owner],
			signal: input.signal
		});
	}
	/**
	* Return the delegated stakes queried by id.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getStakesByIds(input) {
		input.stakedSafeIds.forEach((id) => {
			if (!id || !isValidSafeObjectId(normalizeSafeObjectId(id))) throw new Error(`Invalid Safe Stake id ${id}`);
		});
		return await this.transport.request({
			method: "safex_getStakesByIds",
			params: [input.stakedSafeIds],
			signal: input.signal
		});
	}
	/**
	* Return the latest system state content.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getLatestSafeSystemState({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getLatestSafeSystemState",
			params: [],
			signal
		});
	}
	/**
	* Get events for a given query criteria
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async queryEvents({ query, cursor, limit, order, signal }) {
		if (query && "MoveEventType" in query && hasMvrName(query.MoveEventType)) query = {
			...query,
			MoveEventType: (await this.core.mvr.resolveType({
				type: query.MoveEventType,
				signal
			})).type
		};
		if (query && "MoveEventModule" in query && isValidNamedPackage(query.MoveEventModule.package)) query = {
			...query,
			MoveEventModule: {
				module: query.MoveEventModule.module,
				package: (await this.core.mvr.resolvePackage({
					package: query.MoveEventModule.package,
					signal
				})).package
			}
		};
		if ("MoveModule" in query && isValidNamedPackage(query.MoveModule.package)) query = {
			...query,
			MoveModule: {
				module: query.MoveModule.module,
				package: (await this.core.mvr.resolvePackage({
					package: query.MoveModule.package,
					signal
				})).package
			}
		};
		return await this.transport.request({
			method: "safex_queryEvents",
			params: [
				query,
				cursor,
				limit,
				(order || "descending") === "descending"
			],
			signal
		});
	}
	/**
	* Runs the transaction block in dev-inspect mode. Which allows for nearly any
	* transaction (or Move call) with any arguments. Detailed results are
	* provided, including both the transaction effects and any return values.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async devInspectTransactionBlock(input) {
		let devInspectTxBytes;
		if (isTransaction(input.transactionBlock)) {
			input.transactionBlock.setSenderIfNotSet(input.sender);
			devInspectTxBytes = toBase64(await input.transactionBlock.build({
				client: this,
				onlyTransactionKind: true
			}));
		} else if (typeof input.transactionBlock === "string") devInspectTxBytes = input.transactionBlock;
		else if (input.transactionBlock instanceof Uint8Array) devInspectTxBytes = toBase64(input.transactionBlock);
		else throw new Error("Unknown transaction block format.");
		input.signal?.throwIfAborted();
		return await this.transport.request({
			method: "safe_devInspectTransactionBlock",
			params: [
				input.sender,
				devInspectTxBytes,
				input.gasPrice?.toString(),
				input.epoch
			],
			signal: input.signal
		});
	}
	/**
	* Dry run a transaction block and return the result.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async dryRunTransactionBlock(input) {
		return await this.transport.request({
			method: "safe_dryRunTransactionBlock",
			params: [typeof input.transactionBlock === "string" ? input.transactionBlock : toBase64(input.transactionBlock)]
		});
	}
	/**
	* Return the list of dynamic field objects owned by an object
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getDynamicFields(input) {
		if (!input.parentId || !isValidSafeObjectId(normalizeSafeObjectId(input.parentId))) throw new Error("Invalid Safe Object id");
		return await this.transport.request({
			method: "safex_getDynamicFields",
			params: [
				input.parentId,
				input.cursor,
				input.limit
			],
			signal: input.signal
		});
	}
	/**
	* Return the dynamic field object information for a specified object
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getDynamicFieldObject(input) {
		return await this.transport.request({
			method: "safex_getDynamicFieldObject",
			params: [input.parentId, input.name],
			signal: input.signal
		});
	}
	/**
	* Get the sequence number of the latest checkpoint that has been executed
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getLatestCheckpointSequenceNumber({ signal } = {}) {
		const resp = await this.transport.request({
			method: "safe_getLatestCheckpointSequenceNumber",
			params: [],
			signal
		});
		return String(resp);
	}
	/**
	* Returns information about a given checkpoint
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCheckpoint(input) {
		return await this.transport.request({
			method: "safe_getCheckpoint",
			params: [input.id],
			signal: input.signal
		});
	}
	/**
	* Returns historical checkpoints paginated
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCheckpoints(input) {
		return await this.transport.request({
			method: "safe_getCheckpoints",
			params: [
				input.cursor,
				input?.limit,
				input.descendingOrder
			],
			signal: input.signal
		});
	}
	/**
	* Return the committee information for the asked epoch
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCommitteeInfo(input) {
		return await this.transport.request({
			method: "safex_getCommitteeInfo",
			params: [input?.epoch],
			signal: input?.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getNetworkMetrics({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getNetworkMetrics",
			params: [],
			signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getAddressMetrics({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getLatestAddressMetrics",
			params: [],
			signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getEpochMetrics(input) {
		return await this.transport.request({
			method: "safex_getEpochMetrics",
			params: [
				input?.cursor,
				input?.limit,
				input?.descendingOrder
			],
			signal: input?.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getAllEpochAddressMetrics(input) {
		return await this.transport.request({
			method: "safex_getAllEpochAddressMetrics",
			params: [input?.descendingOrder],
			signal: input?.signal
		});
	}
	/**
	* Return the committee information for the asked epoch
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getEpochs(input) {
		return await this.transport.request({
			method: "safex_getEpochs",
			params: [
				input?.cursor,
				input?.limit,
				input?.descendingOrder
			],
			signal: input?.signal
		});
	}
	/**
	* Returns list of top move calls by usage
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getMoveCallMetrics({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getMoveCallMetrics",
			params: [],
			signal
		});
	}
	/**
	* Return the committee information for the asked epoch
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getCurrentEpoch({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getCurrentEpoch",
			params: [],
			signal
		});
	}
	/**
	* Return the Validators APYs
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getValidatorsApy({ signal } = {}) {
		return await this.transport.request({
			method: "safex_getValidatorsApy",
			params: [],
			signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getChainIdentifier({ signal } = {}) {
		const checkpoint = await this.getCheckpoint({
			id: "0",
			signal
		});
		const bytes = fromBase58(checkpoint.digest);
		return toHex(bytes.slice(0, 4));
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async resolveNameServiceAddress(input) {
		return await this.transport.request({
			method: "safex_resolveNameServiceAddress",
			params: [input.name],
			signal: input.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async resolveNameServiceNames({ format = "dot", ...input }) {
		const { nextCursor, hasNextPage, data } = await this.transport.request({
			method: "safex_resolveNameServiceNames",
			params: [
				input.address,
				input.cursor,
				input.limit
			],
			signal: input.signal
		});
		return {
			hasNextPage,
			nextCursor,
			data: data.map((name) => normalizeSafeNSName(name, format))
		};
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async getProtocolConfig(input) {
		return await this.transport.request({
			method: "safe_getProtocolConfig",
			params: [input?.version],
			signal: input?.signal
		});
	}
	/**
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async verifyZkLoginSignature(input) {
		return await this.transport.request({
			method: "safe_verifyZkLoginSignature",
			params: [
				input.bytes,
				input.signature,
				input.intentScope,
				input.author
			],
			signal: input.signal
		});
	}
	/**
	* Wait for a transaction block result to be available over the API.
	* This can be used in conjunction with `executeTransactionBlock` to wait for the transaction to
	* be available via the API.
	* This currently polls the `getTransactionBlock` API to check for the transaction.
	* @deprecated JSON-RPC APIs are deprecated in the Safe TypeScript SDK. Use `SafeGrpcClient`
	* from `@safecoin/safe/grpc` or `SafeGraphQLClient` from `@safecoin/safe/graphql` instead.
	*/
	async waitForTransaction({ signal, timeout = 6e4, pollInterval = 2e3, ...input }) {
		const timeoutSignal = AbortSignal.timeout(timeout);
		const timeoutPromise = new Promise((_, reject) => {
			timeoutSignal.addEventListener("abort", () => reject(timeoutSignal.reason));
		});
		timeoutPromise.catch(() => {});
		while (!timeoutSignal.aborted) {
			signal?.throwIfAborted();
			try {
				return await this.getTransactionBlock(input);
			} catch {
				await Promise.race([new Promise((resolve) => setTimeout(resolve, pollInterval)), timeoutPromise]);
			}
		}
		timeoutSignal.throwIfAborted();
		throw new Error("Unexpected error while waiting for transaction block.");
	}
};
//#endregion
export { SafeJsonRpcClient, isSafeJsonRpcClient };

//# sourceMappingURL=client.mjs.map